<template>
  <div class="flex gap-2">
    <VaIconMenuCollapsed
      class="cursor-pointer"
      :class="{ 'x-flip': !isSidebarMinimized }"
      :color="collapseIconColor"
      @click="isSidebarMinimized = !isSidebarMinimized"
    />

    <nav class="flex items-center">
      <VaBreadcrumbs>
        <VaBreadcrumbsItem label="Home" :to="{ name: 'admin', params: { orgId: route.params.orgId } }" />
        <VaBreadcrumbsItem
          v-for="item in items"
          :key="item.label"
          :label="item.label"
          :to="!item.isLast ? item.to : undefined"
          :disabled="item.isLast"
        />
      </VaBreadcrumbs>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import VaIconMenuCollapsed from '../icons/VaIconMenuCollapsed.vue'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from '../../stores/global-store'
import { useProjects } from '../../pages/projects/composables/useProjects'
import { useFormsStore } from '../../stores/forms'
import NavigationRoutes from '../sidebar/NavigationRoutes'
import { ProtectedRoute } from '../../config/permissions'

const { isSidebarMinimized } = storeToRefs(useGlobalStore())

// const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const formsStore = useFormsStore()

const { projects, fetchProjects } = useProjects()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
  isLast?: boolean
}

const findRouteName = (name: string) => {
  const traverse = (routers: any[]): string => {
    for (const router of routers) {
      if (router.name === name) {
        return router.displayName
      }
      if (router.children) {
        const result = traverse(router.children)
        if (result) {
          return result
        }
      }
    }
    return ''
  }

  return traverse(NavigationRoutes.routes)
}

const isProjectDetailPage = computed(() => {
  // Проверяем как project-detail, так и его дочерние маршруты (project-forms, project-users, project-info, project-qr-code, project-cleanup)
  return (
    (route.name === ProtectedRoute.PROJECT_DETAIL ||
      route.name === ProtectedRoute.PROJECT_FORMS ||
      route.name === ProtectedRoute.PROJECT_USERS ||
      route.name === ProtectedRoute.PROJECT_INFO ||
      route.name === ProtectedRoute.PROJECT_QR_CODE ||
      route.name === ProtectedRoute.PROJECT_CLEANUP) &&
    route.params.id
  )
})

const isXlsformAssistPage = computed(() => route.name === ProtectedRoute.XLSFORM_ASSIST && route.params.projectId)

const currentProjectName = computed(() => {
  // For project-detail page and its child routes
  if (isProjectDetailPage.value) {
    const projectId = parseInt(route.params.id as string)
    const project = projects.value.find((p) => p.id === projectId)
    return project?.name || 'Project Details'
  }

  // For xlsform-assist and form-* routes
  const isFormRoute = route.name?.toString().startsWith('form-')
  if ((isFormRoute || isXlsformAssistPage.value) && route.params.projectId) {
    const projectId = parseInt(route.params.projectId as string)
    const project = projects.value.find((p) => p.id === projectId)
    return project?.name || 'Project Details'
  }

  return null
})

// Добавляем состояние для данных формы
const formData = ref<any>(null)
const isLoadingForm = ref(false)

// Функция для загрузки данных формы
const loadFormData = async () => {
  const isFormRoute = route.name?.toString().startsWith('form-')
  if (!isFormRoute || !route.params.orgId || !route.params.projectId || !route.params.formId) {
    return
  }

  isLoadingForm.value = true
  try {
    formData.value = await formsStore.fetchFormDetails(
      Number(route.params.orgId),
      Number(route.params.projectId),
      route.params.formId as string,
    )
  } catch (error) {
    console.error('Error loading form data for breadcrumbs:', error)
    formData.value = null
  } finally {
    isLoadingForm.value = false
  }
}

// Загружаем данные формы при изменении маршрута
watch(
  () => route.params,
  () => {
    const isFormRoute = route.name?.toString().startsWith('form-')
    if (isFormRoute) {
      loadFormData()
    } else {
      formData.value = null
    }
  },
  { immediate: true },
)

// Загружаем проекты при инициализации компонента
watch(
  () => route.params.orgId,
  (newOrgId) => {
    if (newOrgId) {
      fetchProjects()
    }
  },
  { immediate: true },
)

const items = computed(() => {
  const result: BreadcrumbNavigationItem[] = []

  // Check if current route has breadcrumb meta
  if (route.meta?.breadcrumb && Array.isArray(route.meta.breadcrumb)) {
    const breadcrumb = route.meta.breadcrumb as Array<{ title: string; path: string }>

    breadcrumb.forEach((item, index) => {
      const isLast = index === breadcrumb.length - 1

      // Replace "Project" and "Form" with actual names
      let label = item.title
      const isFormRoute = route.name?.toString().startsWith('form-')

      if (isProjectDetailPage.value && item.title === 'Project' && currentProjectName.value) {
        label = currentProjectName.value
      } else if (isFormRoute || isXlsformAssistPage.value) {
        if (item.title === 'Project' && currentProjectName.value) {
          label = currentProjectName.value
        } else if (item.title === 'Form' && formData.value?.name) {
          label = formData.value.name
        }
      }

      result.push({
        label: label,
        to: item.path
          ? item.path
              .replace(/:orgId/g, route.params.orgId as string)
              .replace(/:projectId/g, (route.params.projectId as string) || (route.params.id as string))
              .replace(/:id/g, (route.params.id as string) || (route.params.projectId as string))
          : '',
        hasChildren: false,
        isLast: isLast,
      })
    })
    return result
  }

  // Fallback to original logic
  route.matched.forEach((matchedRoute) => {
    if (matchedRoute.name === ProtectedRoute.PROJECT_DETAIL) return

    const labelKey = findRouteName(matchedRoute.name as string)
    if (!labelKey) {
      return
    }

    result.push({
      label: t(labelKey),
      to: matchedRoute.path,
      hasChildren: matchedRoute.children && matchedRoute.children.length > 0,
      isLast: false,
    })
  })

  if (isProjectDetailPage.value) {
    const projectsLabel = findRouteName(ProtectedRoute.PROJECTS)

    const hasProjects = result.some((item) => item.label === t(projectsLabel))

    if (!hasProjects && projectsLabel) {
      result.push({
        label: t(projectsLabel),
        to: `/org/${route.params.orgId}/projects`,
        hasChildren: false,
        isLast: false,
      })
    }

    if (currentProjectName.value) {
      result.push({
        label: currentProjectName.value,
        to: route.fullPath,
        hasChildren: false,
        isLast: true,
      })
    }
  }

  if (result.length > 0 && !isProjectDetailPage.value) {
    result[result.length - 1].isLast = true
  }

  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor('secondary'))
</script>

<style lang="scss" scoped>
.x-flip {
  transform: scaleX(-100%);
}
</style>
